import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import {
  CalendarClock,
  ClipboardCheck,
  FlaskConical,
  ListChecks,
  Package,
  TriangleAlert,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { cn } from '~/utils/cn';

import { isUnder } from '../utils/nav';
import { useNavContext } from '../utils/nav-context';

import { PageHelpProvider } from './page-help';

/** The catalog-v2 sections, in the order an operator works: runs first, setup last. */
const SECTIONS = [
  { value: 'runs', to: '/catalog-v2/runs', labelKey: 'nav.runs', icon: ListChecks },
  { value: 'products', to: '/catalog-v2/products', labelKey: 'nav.products', icon: Package },
  {
    value: 'needs-attention',
    to: '/catalog-v2/needs-attention',
    labelKey: 'nav.needsAttention',
    icon: TriangleAlert,
  },
  {
    value: 'schedules',
    to: '/catalog-v2/schedules',
    labelKey: 'nav.schedules',
    icon: CalendarClock,
  },
  {
    value: 'experiments',
    to: '/catalog-v2/experiments',
    labelKey: 'nav.experiments',
    icon: FlaskConical,
  },
  {
    value: 'feed-audit',
    to: '/catalog-v2/feed-audit',
    labelKey: 'nav.feedAudit',
    icon: ClipboardCheck,
  },
] as const satisfies readonly { value: string; to: string; labelKey: string; icon: LucideIcon }[];

type Section = (typeof SECTIONS)[number]['value'];

/**
 * True inside one run (`/catalog-v2/runs/<id>/…`, not `/runs/new`): the run frame shows a
 * breadcrumb back to Runs instead, so the section tabs would be a third nav layer.
 */
function isInsideRun(pathname: string): boolean {
  return /^\/catalog-v2\/runs\/(?!new(?:\/|$))[^/]+/.test(pathname);
}

/** The section the URL is in; Runs when none matches (the `/catalog-v2` index redirects there).
 *  The tab always shows where you ARE (a product page highlights Products); where you came from
 *  is the page's own "← Back to <origin>" chip. `originSection` only applies on pages that belong
 *  to no section of their own. */
function activeSection(pathname: string, originSection: Section | null): Section {
  // Cost approvals is reached from Needs attention and belongs to it.
  if (isUnder(pathname, '/catalog-v2/cost-approvals')) return 'needs-attention';
  return SECTIONS.find((s) => isUnder(pathname, s.to))?.value ?? originSection ?? 'runs';
}

/** Maps a parsed `NavOrigin.kind` to the top-nav section it should highlight. */
function sectionForOrigin(kind: string | undefined): Section | null {
  switch (kind) {
    case 'batch':
      return 'runs';
    case 'attention':
      return 'needs-attention';
    case 'experiment':
      return 'experiments';
    default:
      return null;
  }
}

/**
 * Catalog-v2 section shell, mounted by `/catalog-v2/route.tsx`: a compact title on
 * the page gradient, then one card whose header strip is the section tab bar
 * (`SegmentedTabs`, underline variant, URL-driven) and whose body scrolls the page.
 */
export function CatalogV2Layout() {
  const { t } = useTranslation('catalog-v2');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // S8 — the origin section (Batches / Needs attention / Experiments) when this page was reached
  // via a cross-section `from` link, so the tab strip agrees with the breadcrumb/back chip.
  const { origin } = useNavContext();
  const originSection = sectionForOrigin(origin?.kind);

  // Larger small-text scale for the catalog preview (utilities.css `html.catalog-v2-type`) — on
  // <html> so portalled popovers/dialogs get it too; removed when leaving the section.
  useEffect(() => {
    document.documentElement.classList.add('catalog-v2-type');
    return () => {
      document.documentElement.classList.remove('catalog-v2-type');
    };
  }, []);

  const options = SECTIONS.map((s) => {
    const Icon = s.icon;
    return {
      value: s.value,
      ariaLabel: t(s.labelKey),
      label: (
        <span className="flex items-center gap-2">
          <Icon aria-hidden="true" className="size-4 shrink-0" />
          {t(s.labelKey)}
        </span>
      ),
    };
  });

  return (
    <PageHelpProvider>
      <div className="bg-gradient-page flex h-full flex-col gap-4 p-6 pt-5">
        <div>
          <h1 className="text-foreground text-xl leading-tight font-semibold">{t('title')}</h1>
          <p className="text-muted-foreground mt-0.5 text-sm leading-5">{t('subtitle')}</p>
        </div>

        <div className="bg-card flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border shadow-sm">
          {!isInsideRun(pathname) && (
            <div className="scrollbar-thin shrink-0 overflow-x-auto px-6 pt-1">
              <SegmentedTabs
                variant="underline"
                value={activeSection(pathname, originSection)}
                onChange={(value) => {
                  const section = SECTIONS.find((s) => s.value === value);
                  if (section) void navigate({ to: section.to });
                }}
                options={options}
                ariaLabel={t('nav.ariaLabel')}
              />
            </div>
          )}

          {/* Inside a batch the card pads tighter (p-4), so its dense lists start higher. */}
          <div
            className={cn(
              'min-h-0 min-w-0 flex-1 overflow-auto',
              isInsideRun(pathname) ? 'p-4' : 'p-6',
            )}
          >
            <Outlet />
          </div>
        </div>
      </div>
    </PageHelpProvider>
  );
}
